/**
 * Règles d'AFFICHAGE sur les pièces d'un dossier — du confort, pas de
 * l'autorité : la base valide (ou refuse) pour de bon. On avertit seulement le
 * staff AVANT l'appel (extrait de casier de plus de 90 jours, assurance échue).
 */
import type { PieceDossier, StatutPiece } from './types'

export const JOURS_VALIDITE_CASIER = 90
const MS_JOUR = 86_400_000

/** « AAAA-MM-JJ » (ou ISO complet) → jour calendaire UTC, en millisecondes. */
function jourUtc(d: string | Date): number | null {
  if (d instanceof Date) {
    return Number.isNaN(d.getTime()) ? null : Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d)
  if (!m) return null
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const v = new Date(t)
  // Refuse les dates impossibles (2026-02-31) que Date.UTC « corrige » en silence.
  return v.getUTCMonth() === Number(m[2]) - 1 && v.getUTCDate() === Number(m[3]) ? t : null
}

/** Nombre de jours calendaires UTC de `depuis` à `jusqua` (négatif si `depuis` est après). */
export function joursEntre(depuis: string | Date, jusqua: string | Date): number | null {
  const a = jourUtc(depuis)
  const b = jourUtc(jusqua)
  return a === null || b === null ? null : Math.round((b - a) / MS_JOUR)
}

export type RaisonCasier = 'trop_ancien' | 'date_future' | 'date_invalide'
export interface ResultatCasier {
  ok: boolean
  /** Âge de l'extrait en jours calendaires (null si la date est illisible). */
  jours: number | null
  raison?: RaisonCasier
}

/**
 * Un extrait de casier est recevable s'il a au plus 90 jours AU JOUR de la
 * validation (90 exactement : accepté ; 91 : refusé) et n'est pas daté du futur.
 */
export function casierRecevable(dateEmission: string | Date, jourValidation: string | Date): ResultatCasier {
  const jours = joursEntre(dateEmission, jourValidation)
  if (jours === null) return { ok: false, jours: null, raison: 'date_invalide' }
  if (jours < 0) return { ok: false, jours, raison: 'date_future' }
  if (jours > JOURS_VALIDITE_CASIER) return { ok: false, jours, raison: 'trop_ancien' }
  return { ok: true, jours }
}

/** Une assurance est valable jusqu'à son échéance INCLUSE. */
export function assuranceRecevable(echeance: string | Date, jourValidation: string | Date): boolean {
  const j = joursEntre(jourValidation, echeance)
  return j !== null && j >= 0
}

export function statutPiece(p: { valide: boolean; motifRejet: string | null }): StatutPiece {
  if (p.valide) return 'valide'
  return p.motifRejet ? 'rejete' : 'en_attente'
}

/** Types exigés sans AUCUNE pièce validée parmi `pieces`. */
export function piecesManquantes(requises: readonly string[], pieces: readonly PieceDossier[]): string[] {
  return requises.filter((t) => !pieces.some((p) => p.type === t && p.statut === 'valide'))
}

export const LIBELLE_STATUT_PIECE: Record<StatutPiece, string> = {
  valide: 'Validée',
  rejete: 'Rejetée',
  en_attente: 'À examiner',
}
